/**
 * A textarea with a preview tab and a paste hook for files.
 *
 * Deliberately not a rich editor: the stored text stays what the engineer typed,
 * and the markdown subset (`lib/markdown.tsx`) is small enough to list in one line
 * under the box. Ctrl+Enter submits, because a reply box that needs the mouse is a
 * reply box people abandon mid-sentence.
 */
import { useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { useFieldControlProps } from '@/components/ui/field'
import { Markdown } from '@/lib/markdown'
import { cn } from '@/lib/cn'

import { pastedFiles } from './AttachmentPicker'

export function MarkdownEditor({
  value,
  onChange,
  onPasteFiles,
  onSubmit,
  rows = 6,
  placeholder,
  disabled,
  required,
  maxLength = 20_000,
  className,
}: {
  value: string
  onChange: (value: string) => void
  /** Files from the clipboard: a screenshot pasted straight into the box. */
  onPasteFiles?: (files: File[]) => void
  /** Ctrl+Enter / Cmd+Enter. */
  onSubmit?: () => void
  rows?: number
  placeholder?: string
  disabled?: boolean
  required?: boolean
  maxLength?: number
  className?: string
}) {
  const { t } = useTranslation()
  const control = useFieldControlProps()
  const [mode, setMode] = useState<'write' | 'preview'>('write')

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onPasteFiles) return
    const files = pastedFiles(event)
    if (files.length === 0) return
    // A file paste is a file paste: do not also insert the placeholder text some
    // browsers put on the clipboard beside a screenshot.
    event.preventDefault()
    onPasteFiles(files)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (onSubmit && event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      onSubmit()
    }
  }

  const tabClass = (active: boolean) =>
    cn(
      'h-9 rounded-md px-3 text-sm',
      active ? 'bg-surface-raised font-medium text-text' : 'text-text-muted hover:text-text',
    )

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-1" role="tablist" aria-label={t('support.editor.tabs')}>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'write'}
          className={tabClass(mode === 'write')}
          onClick={() => setMode('write')}
        >
          {t('support.editor.write')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'preview'}
          className={tabClass(mode === 'preview')}
          onClick={() => setMode('preview')}
          disabled={!value.trim()}
        >
          {t('support.editor.preview')}
        </button>
        <span className="ms-auto text-xs text-text-muted tabular">
          {value.length > maxLength * 0.9 && `${value.length} / ${maxLength}`}
        </span>
      </div>

      {mode === 'write' ? (
        <textarea
          {...control}
          rows={rows}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          className="w-full resize-y rounded-md border border-border bg-surface p-3 text-sm leading-relaxed text-text placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        />
      ) : (
        <div className="min-h-[8rem] rounded-md border border-border bg-surface p-3">
          <Markdown text={value} />
        </div>
      )}

      <p className="text-xs text-text-muted">
        {t('support.editor.hint')}
        {onSubmit && <> · {t('support.editor.shortcut')}</>}
      </p>
    </div>
  )
}
